<template>
  <div class="products">
    <header>
      <IconLibrary
        :name="statusIcons.name"
        :color="statusIcons.color"
        size="sm"
        :hasBorder="true"
      />
      <div>
        <h5 class="title">{{ orderStatus }} ({{ products.length }})</h5>
        <h6>
          Fournisseur
          <span class="corail">{{ seller.identifier || seller.company }}</span>
        </h6>
      </div>
    </header>
    <OrderProduct
      v-for="product in products"
      :key="product.id"
      :hasCheckbox="true"
      @onProductSelect="selectProduct(product)"
      :product="product"
      :order="order"
    />

    <ul class="total">
      <li v-if="shippingFeeIncluded !== null" class="shipping-fee">
        <div>
          <span v-if="shippingFeeIncluded">
            Frais de livraison <strong>inclus</strong> dans le prix
          </span>
          <span v-else>Frais de livraison <strong>non inclus</strong> dans le prix</span>
        </div>
      </li>
      <li>
        <div>
          <strong>{{ $t('order.subtotal') }}:</strong>
          <!-- {{ products.reduce((acc, product) => acc + product.quantity, 0) }}
          {{ $t('order.products') }} -->
        </div>
        <div>
          <MoneyFormat :amount="getTotal(products)" currency="EUR" />
        </div>
      </li>
      <li v-if="matchedTax">
        <div>
          TVA (<strong>{{ matchedTax.tax_rate }}%</strong> de
          <MoneyFormat :amount="matchedTax.subtotal" currency="EUR" />):
        </div>
        <div><MoneyFormat :amount="matchedTax.tax_amount" currency="EUR" /></div>
      </li>
      <li v-if="matchedTax">
        <div><strong>Total (TTC):</strong></div>
        <div>
          <strong><MoneyFormat :amount="matchedTax.total" currency="EUR" /></strong>
        </div>
      </li>
    </ul>
    <footer v-if="!isLoading">
      <!-- Payment info section for buyers (and directors only if they are the buyer) when order is validated but not paid -->
      <!-- Hide this section for card payments as they are already confirmed by Stripe -->
      <section
        v-if="
          isValidated &&
          !isPaid &&
          !isDenied &&
          order.payment_method !== 'card' &&
          (isBuyer || (isDirector && userStore.user._id === order.buyer_id))
        "
        class="payment-info"
      >
        <div class="payment-header">
          <div class="payment-title">
            <IconLibrary name="wire-transfer" size="sm" color="indigo" />
            <h6>Informations de paiement</h6>
          </div>
          <div v-if="isPaidByBuyer" class="payment-badge">
            <IconLibrary name="check" size="xs" />
            <span>Paiement confirmé par l'acheteur</span>
          </div>
        </div>

        <div class="payment-grid">
          <div class="payment-card">
            <div class="card-label">
              <span>IBAN</span>
            </div>
            <div class="card-value">
              {{ seller.iban || 'Non configuré' }}
            </div>
          </div>

          <div class="payment-card">
            <div class="card-label">
              <span>BIC</span>
            </div>
            <div class="card-value">
              {{ seller.bic || 'Non configuré' }}
            </div>
          </div>
        </div>

        <div class="payment-card reference-card">
          <div class="card-label">
            <span>Référence</span>
          </div>
          <div class="card-value reference-value">
            {{ order.identifier }}
          </div>
        </div>

        <div class="payment-actions">
          <!-- Buyer can mark as paid -->
          <button
            v-if="isBuyer && !isPaidByBuyer"
            class="btn btn-primary"
            @click="markAsPaidByBuyer(sellerId)"
          >
            <IconLibrary name="validate" size="sm" />
            J'ai effectué le paiement
          </button>

          <!-- Director can confirm payment only for in-house products (no sellerId) -->
          <button
            v-if="isDirector && !sellerId"
            class="btn btn-primary"
            @click="markAsPaid(sellerId)"
          >
            <IconLibrary name="validate" size="sm" />
            Confirmer le paiement
          </button>

          <!-- Seller can confirm payment for their own products (not for card payments) -->
          <button
            v-if="isSeller && sellerId && order.payment_method !== 'card'"
            class="btn btn-primary"
            @click="markAsPaid(sellerId)"
          >
            <IconLibrary name="validate" size="sm" />
            Confirmer le paiement
          </button>
        </div>
      </section>

      <!-- Director validation section -->
      <section v-if="!isValidated && !isDenied && isDirector" class="is-pending">
        <div class="btn btn-ghost btn-cancel" @click="denyOrder(sellerId)">
          <IconLibrary name="cancel" size="sm" />
          {{ $t('order.reject') }}
        </div>
        <div class="btn" @click="allowOrder(sellerId)">
          <IconLibrary name="validate" size="sm" />
          {{ $t('order.validate') }}
        </div>
      </section>

      <!-- Seller actions -->
      <div class="flex flex-end" v-if="isSeller">
        <section v-if="isValidated && !isFulfilled && !isDenied">
          <div class="btn btn-ghost btn-cancel" @click="denyOrder(sellerId)">
            <IconLibrary name="cancel" size="sm" />
            {{ $t('order.reject') }}
          </div>
        </section>
        <section v-if="!isPaid && isValidated && order.payment_method !== 'card'" class="is-unpaid">
          <div class="btn" @click="payOrder(sellerId)">
            <IconLibrary name="validate" size="sm" />
            {{ $t('order.confirm_payment') }}
          </div>
        </section>
        <section v-if="!isFulfilled && !isDenied">
          <div class="btn" @click="fulfillOrder(sellerId)">
            <IconLibrary name="validate" size="sm" />
            {{ $t('order.fulfill') }}
          </div>
        </section>
      </div>

      <!-- Director actions for in-house products (acts as seller) -->
      <div class="flex flex-end" v-if="isDirector && !sellerId && isValidated">
        <section v-if="!isFulfilled && !isDenied">
          <div class="btn btn-ghost btn-cancel" @click="denyOrder(sellerId)">
            <IconLibrary name="cancel" size="sm" />
            {{ $t('order.reject') }}
          </div>
        </section>
        <section v-if="!isPaid && order.payment_method !== 'card'" class="is-unpaid">
          <div class="btn" @click="payOrder(sellerId)">
            <IconLibrary name="validate" size="sm" />
            {{ $t('order.pay') }}
          </div>
        </section>
        <section v-if="!isFulfilled && !isDenied">
          <div class="btn" @click="fulfillOrder(sellerId)">
            <IconLibrary name="validate" size="sm" />
            {{ $t('order.fulfill') }}
          </div>
        </section>
      </div>
    </footer>
    <footer v-else>
      <Loader />
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useOrderStore } from '../../stores/orders.js';
import { useUserStore } from '../../stores/user.js';
import MoneyFormat from '../Shared/MoneyFormat.vue';
import IconLibrary from '../Shared/IconLibrary.vue';
import Loader from '../Shared/Loader.vue';
import OrderProduct from './OrderProduct.vue';
import { getTotal } from '../../libs/utils';
import { kyWithAuth } from '../../libs/ky';

const orderStore = useOrderStore();
const userStore = useUserStore();
const isLoading = ref(false);

const { products } = defineProps({
  products: {
    type: Array,
    required: true
  }
});

const sellerId = computed(() => products[0]?.seller_id);
const seller = ref({ identifier: 'In-House' });

const isDirector = computed(() => userStore.user?.role === 'director');
const isSeller = computed(() => userStore.user?.role === 'seller');
const isBuyer = computed(() => userStore.user?.role === 'buyer');
const order = computed(() => orderStore.order);

//TODO: This is to make work the tax display before SellerId fix.
//Needs a better solution through the sellerId that should be available now.
const matchedTax = computed(() =>
  order.value.taxes.find((tax) => {
    return tax.subtotal === getTotal(products);
  })
);

const hasProductStatus = (statusKey) => {
  return order.value.products.some(
    (product) => product.seller_id === sellerId.value && product[statusKey]
  );
};

const isPaid = computed(() => hasProductStatus('is_paid'));
const isPaidByBuyer = computed(() => hasProductStatus('is_paid_by_buyer'));
const isValidated = computed(() => hasProductStatus('is_validated'));
const isDenied = computed(() => hasProductStatus('is_denied'));
const isFulfilled = computed(() => hasProductStatus('is_fulfilled'));

const orderStatus = computed(() => {
  if (isDenied.value) return 'Commande refusée';
  if (!isSeller.value && isValidated.value) return 'Commande validée';
  if (isSeller.value && isFulfilled.value) return 'Commande expédiée';
  if (isSeller.value && !isFulfilled.value) return "En attente d'expédition";
  return 'En attente de validation';
});

const statusIcons = computed(() => {
  if (isDenied.value) {
    return { name: 'denied', color: 'red' };
  }
  if (isValidated.value || isFulfilled.value) {
    return { name: 'check', color: 'green' };
  }
  return { name: 'status', color: 'yellow' };
});

const shippingFeeIncluded = computed(() => {
  if (!order.value.shipping_fees) return null;
  const shippingFee = order.value.shipping_fees.find(
    (fee) => fee.seller_id?.toString() === sellerId.value?.toString()
  );
  return shippingFee ? shippingFee.shipping_fee_included : null;
});

onMounted(async () => {
  if (sellerId.value) {
    try {
      const fetchedSeller = await kyWithAuth.get(`users/${sellerId.value}`).json();
      seller.value = fetchedSeller?.user || { identifier: 'In-House' };
      console.log('🐛 Seller data:', seller.value);
      console.log('🐛 IBAN:', seller.value?.iban);
      console.log('🐛 BIC:', seller.value?.bic);
    } catch (e) {
      console.error('Error fetching seller:', e);
    }
  } else {
    // For in-house products, get director's IBAN/BIC
    try {
      console.log('🐛 Order director_id:', order.value?.director_id);

      const directorId = order.value?.director_id;

      if (!directorId) {
        console.error('🐛 No director ID found in order!');
        return;
      }

      const director = await kyWithAuth.get(`users/${directorId}`).json();
      console.log('🐛 Director API response:', director);

      seller.value = {
        identifier: 'In-House',
        iban: director?.user?.iban,
        bic: director?.user?.bic
      };
      console.log('🐛 Final in-house seller value:', seller.value);
    } catch (e) {
      console.error('Error fetching director:', e);
    }
  }
});

const apiCallWrapper = async (callback) => {
  isLoading.value = true;
  try {
    await callback();
  } catch (e) {
    console.error('Error', e);
  } finally {
    setTimeout(() => (isLoading.value = false), 500);
  }
};

const updateOrderStatus = async ({ statusKey, sellerId, copy = false }) => {
  const updatedProducts = order.value.products.map((product) => ({
    ...product,
    [statusKey]:
      product.seller_id === sellerId || (!product.seller_id && !sellerId)
        ? true
        : product[statusKey]
  }));

  await orderStore.updateOrderProducts({
    orderId: order.value._id,
    sellerId,
    updatedProducts,
    copy
  });
};

const allowOrder = (sellerId) =>
  apiCallWrapper(() => updateOrderStatus({ statusKey: 'is_validated', sellerId, copy: true }));
const denyOrder = (sellerId) =>
  apiCallWrapper(() => updateOrderStatus({ statusKey: 'is_denied', sellerId }));
const payOrder = (sellerId) =>
  apiCallWrapper(() => updateOrderStatus({ statusKey: 'is_paid', sellerId }));
const fulfillOrder = (sellerId) =>
  apiCallWrapper(() => updateOrderStatus({ statusKey: 'is_fulfilled', sellerId }));
const markAsPaid = (sellerId) =>
  apiCallWrapper(() => updateOrderStatus({ statusKey: 'is_paid', sellerId }));
const markAsPaidByBuyer = (sellerId) =>
  apiCallWrapper(() => updateOrderStatus({ statusKey: 'is_paid_by_buyer', sellerId }));
</script>

<style scoped lang="scss">
.products {
  margin: 0 0 25px 0;
  background-color: $white;
  border-radius: $base-radius;
  box-shadow: 0px 8px 18px rgba(56, 71, 162, 0.08);

  > * {
    padding: 15px 25px;
  }

  header {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 25px 25px 0 25px;
    margin-bottom: 15px;
    h5,
    h6 {
      margin: 0;
    }
    h6 {
      color: $monsoon;
      font-weight: $font-weight-light;
    }
  }

  footer {
    background-color: $brand-light-indigo;
    border-top: 1px solid darken($brand-light-indigo, 15%);
    padding: 15px 25px;
    margin-top: 20px;
    > section {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 10px;
    }
    .payment-info {
      margin-bottom: 20px;
      flex-direction: column;
      justify-content: flex-start;

      .payment-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 5px;
        width: 100%;

        .payment-title {
          display: flex;
          align-items: center;
          gap: 8px;
          h6 {
            margin: 0;
            color: $brand-indigo;
            font-weight: 600;
            font-size: 16px;
          }
        }

        .payment-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          background: $brand-light-mint;
          color: $brand-api-green;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
        }
      }

      .payment-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        width: 100%;
        margin-bottom: 5px;

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }
      }

      .payment-card {
        background: $white;
        border: 1px solid $gainsboro;
        border-radius: 8px;
        padding: 16px;
        transition: all 0.2s ease;

        &:hover {
          border-color: $brand-blue;
          box-shadow: 0 2px 8px rgba($brand-blue, 0.1);
        }

        .card-label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;

          span {
            font-size: 12px;
            font-weight: 500;
            color: $brand-grey;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }

        .card-value {
          font-size: 14px;
          font-weight: 600;
          color: $brand-deep-purple;
          word-break: break-all;
        }

        &.reference-card {
          border-color: $brand-corail;
          width: 100%;
          background: linear-gradient(
            135deg,
            rgba($brand-corail, 0.05) 0%,
            rgba($brand-corail, 0.02) 100%
          );

          .reference-value {
            color: $brand-corail;
            font-weight: 700;
          }
        }
      }

      .payment-actions {
        display: flex;
        justify-content: flex-end;

        .btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;

          &.btn-primary {
            background: linear-gradient(135deg, $brand-blue 0%, darken($brand-blue, 10%) 100%);
            color: $white;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba($brand-blue, 0.3);
            }

            &:active {
              transform: translateY(0);
            }
          }
        }
      }
    }
  }
  .total {
    border-top: 1px solid darken($brand-light-indigo, 15%);
    margin: 20px 30px;
    padding: 15px 10px;
  }
  .total li {
    display: flex;
    gap: 35px;
    padding: 6px 0px 6px 0px;
    justify-content: flex-end;

    > *:last-child {
      min-width: 80px;
      text-align: right;
    }
  }
  .corail {
    color: $brand-corail;
  }
  .flex-end {
    justify-content: flex-end;
    gap: 1rem;
  }
}
.shipping-fee {
  font-size: 90%;
}
</style>
